import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { formatApiError } from '../services/api';
import CaptchaWidget from '../components/CaptchaWidget';
import { PillInput, PillButton } from '../components/PillKit';
import { CaretLeft, ShieldCheck } from '@phosphor-icons/react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_moneyswap-9/artifacts/pvoha9c5_D%24.png";

function AuthShell({ children, back }) {
  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      <header className="px-5 pt-6 pb-2 flex items-center justify-between">
        {back ? (
          <Link to={back} className="text-white p-1" aria-label="Back">
            <CaretLeft className="w-6 h-6" />
          </Link>
        ) : (
          <span className="w-6" />
        )}
        <Link to="/"><img src={LOGO_URL} alt="Divispend" className="h-10 w-auto" /></Link>
        <span className="w-6" />
      </header>
      <main className="flex-1 max-w-md w-full mx-auto px-6 pt-6 pb-12">{children}</main>
    </div>
  );
}

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const captchaRef = useRef(null);

  const [show2FA, setShow2FA] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [useBackup, setUseBackup] = useState(false);
  const [backupCode, setBackupCode] = useState('');

  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (showCaptcha && !captchaToken) return setError('Please complete the CAPTCHA challenge.');
    setLoading(true);
    try {
      const data = await login(email, password, undefined, undefined, captchaToken);
      if (data.requires_2fa) { setShow2FA(true); setLoading(false); return; }
      if (data.reset_required) {
        navigate('/forced-password-reset', { state: { userId: data.user_id, resetTicket: data.reset_ticket } });
        setLoading(false);
        return;
      }
      navigate('/dashboard');
    } catch (err) {
      setError(formatApiError(err));
      const next = failedAttempts + 1;
      setFailedAttempts(next);
      if (next >= 2) setShowCaptcha(true);
      captchaRef.current?.reset();
      setCaptchaToken(null);
    } finally { setLoading(false); }
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const code = useBackup ? undefined : totpCode;
      const backup = useBackup ? backupCode : undefined;
      const data = await login(email, password, code, backup);
      if (data.requires_2fa) { setError('Invalid code. Please try again.'); setLoading(false); return; }
      navigate('/dashboard');
    } catch (err) {
      setError(formatApiError(err));
    } finally { setLoading(false); }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // ============ 2FA Step ============
  if (show2FA) {
    return (
      <AuthShell back="/login">
        <button onClick={() => { setShow2FA(false); setTotpCode(''); setBackupCode(''); setUseBackup(false); setError(''); }}
                className="text-white/60 text-sm mb-6 flex items-center gap-1" data-testid="back-to-login">
          <CaretLeft className="w-4 h-4" /> Back to login
        </button>
        <div className="flex items-center gap-3 mb-6">
          <ShieldCheck className="w-9 h-9 text-[#22C55E]" weight="fill" />
          <div>
            <h1 className="text-2xl font-extrabold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>Two-Factor</h1>
            <p className="text-[#22C55E] text-sm font-bold">Enter your code</p>
          </div>
        </div>
        <p className="text-white/60 text-sm mb-6">
          {useBackup ? 'Enter one of your backup recovery codes.' : 'Enter the 6-digit code from your authenticator app.'}
        </p>

        {error && <div className="mb-4 p-3 bg-red-500/10 border-[2px] border-red-500/40 rounded-2xl text-red-400 text-sm" data-testid="auth-error">{error}</div>}

        <form onSubmit={handle2FASubmit} className="space-y-4">
          {useBackup ? (
            <PillInput
              type="text"
              value={backupCode}
              onChange={(e) => setBackupCode(e.target.value.toUpperCase())}
              placeholder="XXXX-XXXX"
              maxLength={9}
              autoFocus
              data-testid="backup-code-input"
            />
          ) : (
            <PillInput
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={totpCode}
              onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
              placeholder="000000"
              className="text-3xl tracking-[0.5em] text-center"
              autoFocus
              data-testid="totp-code-input"
            />
          )}
          <PillButton onClick={handle2FASubmit} disabled={loading} data-testid="verify-2fa-btn">
            {loading ? 'Verifying…' : 'Verify'}
          </PillButton>
          <button type="button" onClick={() => { setUseBackup(!useBackup); setError(''); }} className="w-full text-white/60 text-sm hover:text-white">
            {useBackup ? 'Use authenticator app instead' : 'Use a backup code instead'}
          </button>
        </form>
      </AuthShell>
    );
  }

  // ============ Login Step ============
  return (
    <AuthShell back="/">
      <div className="mb-8">
        <h1 className="text-4xl font-extrabold tracking-tight text-white leading-none" style={{ fontFamily: 'Cabinet Grotesk' }}>
          Welcome Back
        </h1>
        <p className="text-[#22C55E] text-xl font-bold mt-1.5" style={{ fontFamily: 'Cabinet Grotesk' }}>
          Sign in to your account
        </p>
      </div>

      {error && <div className="mb-4 p-3 bg-red-500/10 border-[2px] border-red-500/40 rounded-2xl text-red-400 text-sm" data-testid="auth-error">{error}</div>}

      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label className="text-xs tracking-[0.18em] uppercase font-bold text-white/60 mb-2 block">Email</label>
          <PillInput
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            autoComplete="email"
            required
            data-testid="login-email-input"
          />
        </div>
        <div>
          <label className="text-xs tracking-[0.18em] uppercase font-bold text-white/60 mb-2 block">Password</label>
          <PillInput
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Your password"
            autoComplete="current-password"
            required
            data-testid="login-password-input"
          />
        </div>

        <div className="flex justify-end -mt-1">
          <Link to="/forgot-password" className="text-xs text-[#22C55E] hover:underline font-semibold" data-testid="login-forgot-password-link">
            Forgot password?
          </Link>
        </div>

        {showCaptcha && (
          <div data-testid="login-captcha-block">
            <p className="text-xs text-[#F59E0B] mb-2">Please verify you&apos;re not a robot.</p>
            <CaptchaWidget ref={captchaRef} onVerify={(t) => setCaptchaToken(t)} />
          </div>
        )}

        <PillButton onClick={handleSubmit} disabled={loading || (showCaptcha && !captchaToken)} data-testid="login-submit-button">
          {loading ? 'Signing in…' : 'Sign In'}
        </PillButton>
      </form>

      {/* Divider */}
      <div className="flex items-center gap-3 my-6">
        <div className="flex-1 h-px bg-white/10" />
        <span className="text-white/40 text-xs uppercase tracking-widest">or</span>
        <div className="flex-1 h-px bg-white/10" />
      </div>

      <button
        onClick={handleGoogleLogin}
        className="w-full bg-white text-black rounded-full px-6 py-4 text-base font-bold tracking-tight hover:bg-white/90 flex items-center justify-center gap-3"
        data-testid="google-login-button"
      >
        <svg className="w-5 h-5" viewBox="0 0 24 24">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
        </svg>
        Continue with Google
      </button>

      <p className="mt-8 text-center text-sm text-white/60">
        Don&apos;t have an account?{' '}
        <Link to="/register" className="text-[#22C55E] font-bold hover:underline" data-testid="login-register-link">
          Sign up
        </Link>
      </p>
    </AuthShell>
  );
}
